'use client'

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEYS = {
    VISITOR_ID: 'ecommaps_visitor_id',
    SESSION_ID: 'ecommaps_session_id',
    SESSION_EXPIRY: 'ecommaps_session_expiry',
    TRAFFIC_SOURCE: 'ecommaps_traffic_source', // Stores the first touch source
}

const SESSION_DURATION_MS = 30 * 60 * 1000 // 30 minutes

interface AnalyticsEvent {
    event_type: string
    meta?: Record<string, any>
}

interface TrafficData {
    source: string
    medium: string
    campaign: string
}

export function useAnalytics(siteId: string) {
    const [visitorId, setVisitorId] = useState<string | null>(null)
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [trafficData, setTrafficData] = useState<TrafficData | null>(null)

    // Helper: Parse Traffic Source (UTM > Referrer > Direct)
    const getTrafficSource = (): TrafficData => {
        if (typeof window === 'undefined') return { source: 'direct', medium: '(none)', campaign: '' }

        const searchParams = new URLSearchParams(window.location.search)
        const referrer = document.referrer

        // 1. Check UTMs (The Gold Standard)
        if (searchParams.has('utm_source')) {
            return {
                source: searchParams.get('utm_source') || 'manual',
                medium: searchParams.get('utm_medium') || '(none)',
                campaign: searchParams.get('utm_campaign') || ''
            }
        }

        // 2. Check Referrer (The Fallback)
        if (referrer) {
            try {
                const refUrl = new URL(referrer)
                // Ignore internal navigation (same domain)
                if (refUrl.hostname !== window.location.hostname) {
                    // Detect Social/Search common patterns (Basic Sniffing)
                    const host = refUrl.hostname.toLowerCase()
                    let medium = 'referral'

                    if (host.includes('google') || host.includes('bing') || host.includes('yahoo')) medium = 'organic'
                    if (host.includes('facebook') || host.includes('instagram') || host.includes('t.co') || host.includes('twitter') || host.includes('linkedin')) medium = 'social'

                    return {
                        source: host,
                        medium: medium,
                        campaign: ''
                    }
                }
            } catch (e) {
                // Invalid URL, ignore
            }
        }

        // 3. Direct / Internal
        return { source: 'direct', medium: '(none)', campaign: '' }
    }

    // Initialize Identity & Traffic
    useEffect(() => {
        if (!siteId) return
        if (typeof window === 'undefined') return

        // 1. Visitor ID
        let vid = localStorage.getItem(STORAGE_KEYS.VISITOR_ID)
        if (!vid) {
            vid = crypto.randomUUID()
            localStorage.setItem(STORAGE_KEYS.VISITOR_ID, vid)
        }
        setVisitorId(vid)

        // 2. Session ID
        const now = Date.now()
        let storedSid = localStorage.getItem(STORAGE_KEYS.SESSION_ID)
        let expiry = localStorage.getItem(STORAGE_KEYS.SESSION_EXPIRY)

        if (!storedSid || !expiry || now > parseInt(expiry)) {
            // New Session
            storedSid = crypto.randomUUID()
            localStorage.setItem(STORAGE_KEYS.SESSION_ID, storedSid)
        }

        // Refresh Expiry
        localStorage.setItem(STORAGE_KEYS.SESSION_EXPIRY, (now + SESSION_DURATION_MS).toString())
        setSessionId(storedSid)

        // 3. Traffic Source Logic (First-Touch Attribution Model)
        // We only analyze traffic on "New Session" or if missing.
        // Actually, for better accuracy, let's parse it on every landing, 
        // but if it's "direct" and we already have a source in session, likely keep the old one?
        // STRATEGY: Last Non-Direct Click is common. But let's start with "Current Session Source".

        let storedTraffic = sessionStorage.getItem(STORAGE_KEYS.TRAFFIC_SOURCE)
        let traffic: TrafficData

        if (storedTraffic) {
            // We have a source for this browser session. 
            // OPTIONAL: Check if new UTMs override it (e.g. user clicked a new ad).
            const currentTraffic = getTrafficSource()
            if (currentTraffic.source !== 'direct') {
                // New explicit source overrides previous session source (Last Non-Direct Click within Session)
                traffic = currentTraffic
                sessionStorage.setItem(STORAGE_KEYS.TRAFFIC_SOURCE, JSON.stringify(traffic))
            } else {
                traffic = JSON.parse(storedTraffic)
            }
        } else {
            // first page view of session
            traffic = getTrafficSource()
            sessionStorage.setItem(STORAGE_KEYS.TRAFFIC_SOURCE, JSON.stringify(traffic))
        }
        setTrafficData(traffic)

    }, [siteId])


    const trackEvent = useCallback(async (eventType: string, meta: Record<string, any> = {}) => {
        if (!siteId || !visitorId || !sessionId) {
            return
        }

        try {
            // Refresh session persistence
            localStorage.setItem(STORAGE_KEYS.SESSION_EXPIRY, (Date.now() + SESSION_DURATION_MS).toString())

            const payload = {
                site_id: siteId,
                event_type: eventType,
                path: window.location.pathname,
                visitor_id: visitorId,
                session_id: sessionId,
                // Inject Traffic Data
                traffic_source: trafficData?.source,
                traffic_medium: trafficData?.medium,
                traffic_campaign: trafficData?.campaign,
                meta: {
                    ...meta,
                    referrer: document.referrer,
                    userAgent: navigator.userAgent,
                    screen: `${window.screen.width}x${window.screen.height}`,
                    language: navigator.language
                }
            }

            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
            const success = navigator.sendBeacon('/api/analytics/track', blob)

            if (!success) {
                await fetch('/api/analytics/track', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    keepalive: true
                })
            }

        } catch (error) {
            console.error('[Analytics] Track Error:', error)
        }
    }, [siteId, visitorId, sessionId, trafficData])

    const trackPageView = useCallback((path: string) => {
        trackEvent('page_view', { path })
    }, [trackEvent])

    return {
        trackEvent,
        trackPageView,
        visitorId,
        sessionId
    }
}
